import { useFieldArray } from 'react-hook-form'
import { Add, Close } from '@mui/icons-material'
import {
  Button,
  FormControl,
  FormLabel,
  IconButton,
  Stack,
  Typography,
  Paper,
} from '@mui/material'
import { getDefaultValue } from '@/utils/rules'
import PropertyField, { PropertyFieldProps } from './PropertyField'

type PropertyMultiObjectProps = PropertyFieldProps

export default function PropertyMultiObject({
  propertyId,
  property,
  required,
  shrinkLabel,
  variant,
  dependencyValues,
}: PropertyMultiObjectProps) {
  const minItems = property.minItems || 0
  const { fields, append, remove } = useFieldArray({
    name: propertyId,
    rules: {
      required,
      validate: minItems
        ? (value) => {
            if (!Array.isArray(value)) return false
            if (value.length < minItems) {
              return `At least ${minItems} item${
                minItems > 1 ? 's are' : ' is'
              } required`
            }
            return true
          }
        : undefined,
    },
  })
  const { label, description } = property.display

  // Get the schema for items in the array
  const itemsSchema = property.items?.properties || {}
  const itemsRequired = property.items?.required || []

  // Create default object based on items schema
  const createDefaultObject = () => {
    const defaultObj: Record<string, unknown> = {}
    Object.entries(itemsSchema).forEach(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ([key, itemProperty]: [string, any]) => {
        defaultObj[key] = getDefaultValue(itemProperty)
      },
    )
    return defaultObj
  }

  return (
    <FormControl sx={{ rowGap: 1, width: '100%', pb: 1 }}>
      <Stack>
        {label && <FormLabel>{label}</FormLabel>}
        {description && (
          <Typography variant="caption" color="text.secondary">
            {description}
          </Typography>
        )}
      </Stack>

      <Button
        onClick={() => append(createDefaultObject())}
        startIcon={<Add />}
        sx={{
          width: 'fit-content',
        }}
        variant="text"
      >
        Add Item
      </Button>

      <Stack spacing={2}>
        {fields.map((field, index) => {
          // Sort properties by sort_order if it exists
          const sortedProperties = Object.entries(itemsSchema).sort(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            ([, a]: [string, any], [, b]: [string, any]) => {
              const orderA = a.sort_order ?? 0
              const orderB = b.sort_order ?? 0
              return orderA - orderB
            },
          )

          return (
            <Paper key={field.id} sx={{ p: 2 }} variant="outlined">
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography variant="subtitle2" color="text.secondary">
                  Option {index + 1}
                </Typography>
                <IconButton onClick={() => remove(index)} size="small">
                  <Close fontSize="small" />
                </IconButton>
              </Stack>

              <Stack spacing={2.5} sx={{ mt: 2 }}>
                {sortedProperties.map(
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  ([itemPropertyId, itemProperty]: [string, any]) => {
                    const isRequired = itemsRequired.includes(itemPropertyId)
                    return (
                      <PropertyField
                        key={itemPropertyId}
                        propertyId={`${propertyId}.${index}.${itemPropertyId}`}
                        property={itemProperty}
                        required={isRequired}
                        shrinkLabel={shrinkLabel}
                        variant={variant}
                        dependencyValues={dependencyValues}
                      />
                    )
                  },
                )}
              </Stack>
            </Paper>
          )
        })}
      </Stack>

      {fields.length === 0 && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ fontStyle: 'italic' }}
        >
          No items added yet. Click &quot;Add Item&quot; to get started.
        </Typography>
      )}
    </FormControl>
  )
}
